import type * as React from 'react';
import { ChevronLeft } from 'lucide-react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../lib/cn.ts';

/*
 * The way out of a detail page, in the gap PageBody leaves above its first
 * child. Quiet by design: it is the way back, not an action, so it darkens on
 * hover rather than announcing itself.
 *
 * Exported as variants too, because on a routed page the element is the
 * consumer's — a router Link, the same arrangement as tabVariants and
 * railLinkVariants.
 */
export const backLinkVariants = cva([
  'inline-flex items-center gap-1.5 py-1 text-sm font-semibold text-ink-muted transition-colors',
  /* On a phone the hit area grows to a fingertip's 44px; the text stays where it is. */
  'relative max-mobile:before:absolute max-mobile:before:-inset-x-2 max-mobile:before:-inset-y-2.5',
  'hover:text-ink focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink-faint',
  '[&_svg]:size-3.5 [&_svg]:shrink-0',
]);

export type BackLinkVariants = VariantProps<typeof backLinkVariants>;

export type BackLinkProps = Omit<React.ComponentProps<'a'>, 'style'>;

export function BackLink({ className, children, ...props }: BackLinkProps) {
  return (
    <a data-slot='back-link' className={cn(backLinkVariants(), className)} {...props}>
      <ChevronLeft aria-hidden='true' />
      {children}
    </a>
  );
}

export type BackButtonProps = Omit<React.ComponentProps<'button'>, 'style'>;

/*
 * The same way back, for a screen that decides for itself where back is — a
 * popup closing onto the page under it, rather than a route to navigate to.
 */
export function BackButton({
  className,
  children,
  type = 'button',
  ...props
}: BackButtonProps) {
  return (
    <button
      data-slot='back-button'
      type={type}
      className={cn(backLinkVariants(), 'cursor-pointer', className)}
      {...props}
    >
      <ChevronLeft aria-hidden='true' />
      {children}
    </button>
  );
}
